import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LineupImportsBatchStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useCancelImportLineup } from './useCancelImportLineup.ts';
import { useConfirmImportLineup } from './useConfirmImportLineup.ts';

const Root = styled(Horizontal)`
  justify-content: flex-end;
  padding: var(--double-unit);
  border-top: 1px solid var(--c-nd-200);
`;

type Props = {
  id?: string | null;
  lineupsCount?: number;
  lineupsImportState?: So5LineupImportsBatchStatus;
  onClose: () => void;
  onConfirm: () => void;
  onComplete: () => void;
};

export const ImportLineupActions = ({
  id,
  lineupsCount,
  lineupsImportState,
  onClose,
  onConfirm,
  onComplete,
}: Props) => {
  const confirmImportLineup = useConfirmImportLineup(id);
  const cancelImportLineup = useCancelImportLineup(id);

  if (
    !id ||
    !lineupsCount ||
    !lineupsImportState ||
    [
      So5LineupImportsBatchStatus.DECLINED,
      So5LineupImportsBatchStatus.EXPIRED,
    ].includesValue(lineupsImportState)
  )
    return null;

  const isReady = [
    So5LineupImportsBatchStatus.PREPARED,
    So5LineupImportsBatchStatus.PREPARING,
  ].includesValue(lineupsImportState);

  const isLoading =
    So5LineupImportsBatchStatus.PROCESSING === lineupsImportState;
  const isComplete =
    So5LineupImportsBatchStatus.PROCESSED === lineupsImportState;

  return (
    <Root>
      {!!isReady && (
        <Button
          size="medium"
          onClick={() => {
            cancelImportLineup();
            onClose();
          }}
          color="tertiary"
        >
          <FormattedMessage {...glossary.decline} />
        </Button>
      )}
      <Button
        loading={So5LineupImportsBatchStatus.PREPARING === lineupsImportState}
        size="medium"
        color="secondary"
        onClick={() => {
          if (isReady) {
            confirmImportLineup();
            onConfirm();
          }
          if (isComplete) {
            onClose();
            onComplete();
          }
        }}
      >
        {!!isReady && (
          <FormattedMessage
            defaultMessage="Import {lineupsCount} {lineupsCount, plural, one {lineup} other {lineups}}"
            values={{ lineupsCount }}
          />
        )}
        {!!isLoading && <FormattedMessage {...glossary.loading} />}
        {!!isComplete && <FormattedMessage {...glossary.gotIt} />}
      </Button>
    </Root>
  );
};
